import { ref, computed, useContext } from '@nuxtjs/composition-api'

export default function useDynamicOptions(dynamicOptionsConfigRaw = {}) {
  const { store, $axios } = useContext()

  const isLoading = ref(false)
  const options = ref([])

  const dynamicOptionsConfig = computed(() => dynamicOptionsConfigRaw.value || dynamicOptionsConfigRaw)
  const school = computed(() => store.state.settings.school)
  const fieldName = computed(() => dynamicOptionsConfig.value.attributeFieldName)
  const type = computed(() => dynamicOptionsConfig.value.attributeType)
  const persistValue = computed(() => dynamicOptionsConfig.value.persistValue)
  const displayValue = computed(() => dynamicOptionsConfig.value.displayValue)

  /**
   * Fetches options from integration attribute mappings API
   *
   * This function makes an API call to retrieve attribute mappings based on
   * the current school, field name, and attribute type. It updates the options
   * ref with the response data.
   *
   * @async
   * @returns {Promise<void>} - A promise that resolves when the options are fetched
   * @throws {Error} - Rethrows any errors that occur during the API call
   */
  async function fetchOptionsFromIntegrationAttributeMappings () {
    isLoading.value = true
    try {
      const params = {
        school: school.value,
        fieldName: fieldName.value,
        type: type.value,
      }
      const res = await $axios.get(`${school.value}/integration/attributeMappings`, { params })
      options.value = Object.values(res?.data)
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Retrieves an object from options that matches the given value
   *
   * @param {string} value - The value to search for in options
   * @returns {Object} - The matching option object or an empty object if not found
   */
  function getObjectValue (value) {
    if (!value) return {}
    if (!persistValue.value) return {}
    return options.value.find((option) => option[persistValue.value] === value) || {}
  }

  /**
   * Retrieves multiple option objects based on an array of values
   *
   * @param {string|Array<string>} values - Array of values or single value to search for
   * @returns {Array<Object>} - Array of matching option objects
   */
  function getObjectValues (values = []) {
    const normalizedValues = Array.isArray(values) ? values : [values]
    return normalizedValues.filter(Boolean).map((value) => getObjectValue(value))
  }

  /**
   * Gets the display value for a given value based on the configured display field
   *
   * @param {string} value - The value to get a display value for
   * @returns {string} - The display value or the original value if not found
   */
  function getDisplayValue (value) {
    const objectValue = getObjectValue(value)
    if (!objectValue) return value
    return objectValue[displayValue.value] || value
  }

  /**
   * Gets display values for multiple values
   *
   * @param {string|Array<string>} values - Array of values or single value to get display values for
   * @returns {Array<string>} - Array of display values
   */
  function getDisplayValues (values = []) {
    const normalizedValues = Array.isArray(values) ? values : [values]
    return normalizedValues.filter(Boolean).map((value) => getDisplayValue(value))
  }

  /**
   * Creates a computed property that returns display values for the given value(s)
   *
   * @param {string|Array<string>} valueOrValues - Value or array of values to create a computed property for
   * @returns {ComputedRef<Array<string>>} - Computed property that returns display values
   */
  function getDisplayValueComputed (valueOrValues) {
    return computed(() => getDisplayValues(valueOrValues))
  }

  return {
    dynamicOptionsConfig,
    options,
    isLoading,
    fetchOptionsFromIntegrationAttributeMappings,
    getObjectValue,
    getObjectValues,
    getDisplayValue,
    getDisplayValues,
    getDisplayValueComputed,
  }
}
